import {
  Command,
  Editor,
  Plugin,
  DocumentChangeEvent,
  Node,
  first,
} from "ckeditor5";
import { cloneDeep } from "lodash";

import { exists } from "@examspark/common";

import { collectModel } from "../util/document";

type HtmlAttributes = {
  classes?: string[];
};

export class ScrubUnsupportedContentCommand extends Command {
  override refresh() {
    this.isEnabled = this.editor.hasUnsupportedContent ?? false;
  }

  override execute() {
    const elements = collectUnsupportedContent(this.editor);

    if (elements.length === 0) return;

    this.editor.model.change((writer) => {
      for (const element of elements) {
        if (element.is("element")) {
          if (element.name === "htmlDivParagraph") {
            //
            // Special handling for divs - replace them with regular paragraphs
            //
            writer.rename(element, "paragraph");
          } else if (element.name.startsWith("html")) {
            writer.unwrap(element);
            continue;
          }
        }

        for (const [key] of element.getAttributes()) {
          if (key.startsWith("html")) {
            writer.removeAttribute(key, element);
          }
        }
      }
    });
  }
}

export default class UnsupportedContent extends Plugin {
  private _hasUnsupportedContent: boolean | undefined;

  init() {
    Object.defineProperty(this.editor, "hasUnsupportedContent", {
      get: () => this._hasUnsupportedContent,
    });

    this.editor.model.document.on<DocumentChangeEvent>("change:data", () => {
      this._hasUnsupportedContent = this.checkForUnsupportedContent();
    });

    this.editor.commands.add(
      "scrubUnsupportedContent",
      new ScrubUnsupportedContentCommand(this.editor),
    );
  }

  checkForUnsupportedContent() {
    if (!this.editor.config.get("detectUnsupportedHtml")) return undefined;

    const elements = collectUnsupportedContent(this.editor);

    return elements.length > 0;
  }
}

const collectUnsupportedContent = (editor: Editor) => {
  //
  // Best-effort simple detection of unsupported HTML:
  // Look for elements or attributes whose names start with `html`
  //
  // These are created by the "General HTML Support" plugin and indicate the
  // presence of something it's trying to round-trip because it isn't handled
  // by any other plugin
  //

  const isUnsupportedHtmlElement = (node: Node) => {
    if (!node.is("element") || !node.name.startsWith("html")) return false;

    if (node.name === "htmlDivParagraph") {
      //
      // divs are considered "supported" only if they have no attributes
      //
      return first(node.getAttributeKeys()) != null;
    }

    return true;
  };

  const hasUnsupportedHtmlAttributes = (node: Node) => {
    for (const [name, value] of node.getAttributes()) {
      if (!name.startsWith("html")) continue;
      if (value == null) continue;
      if (typeof value !== "object") return true;

      return isUnsupportedHtmlAttribute(name, value);
    }

    return false;
  };

  const isUnsupportedHtmlAttribute = (name: string, value: HtmlAttributes) => {
    //
    // Clone the value so we don't mess it up spelunking through it
    //
    const clonedValue = cloneDeep(value);

    if (name === "htmlTableAttributes") {
      //
      // There appears to be a bug with the table plugin where it doesn't consume
      // the ck-table-resized class correctly, and it ends up in an html attribute
      //
      clonedValue.classes = cleanClasses(
        clonedValue.classes,
        "ck-table-resized",
      );
    }

    const styleDefinitions = editor.config.get("style.definitions");
    if (styleDefinitions != null) {
      const styleClasses = styleDefinitions.flatMap((s) => s.classes);
      clonedValue.classes = cleanClasses(clonedValue.classes, ...styleClasses);
    }

    return Object.values(clonedValue).filter(exists).length > 0;
  };

  return collectModel(
    editor.model.document.getRoot(),
    (e) => isUnsupportedHtmlElement(e) || hasUnsupportedHtmlAttributes(e),
  );
};

const cleanClasses = (classes?: string[], ...classNames: string[]) => {
  if (classes == null) return undefined;
  classes = classes.filter((c) => !classNames.includes(c));
  if (classes.length === 0) return undefined;
  return classes;
};

declare module "ckeditor5" {
  interface Editor {
    hasUnsupportedContent: boolean | undefined;
  }

  interface EditorConfig {
    detectUnsupportedHtml?: boolean;
  }
}
